import { Box, Button, Checkbox, Group, NumberInput, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ContractLine } from '@/api/generated/models';
import type { SpaceLinePlanUsage } from '@/features/work-packages/workPackageDisplay';
import {
  getListContractLinesQueryKey,
  useListContractLines
} from '@/api/generated/contracts/contracts';
import { useListUnits } from '@/api/generated/units/units';
import { FormModal, ModalFooter, SelectField, TextField } from '@/components/common';
import { findUnitLabel } from '@/features/units/unitDisplay';
import { invalidateContractSpaceUsage } from '@/features/work-packages/workPackageDisplay';

interface WorkPackageItemPickerModalProps {
  opened: boolean;
  onClose: () => void;
  contractId?: string;
  existingContractLineIds: string[];
  /** Kad je zadano, nudi SAMO stavke iz ovog seta — koristi WorkPackageDetailPage da izravno
   * dodavanje (post-aktivacijski) ne nudi artikle koji nikad nisu planirani baš za taj prostor
   * (vidi contract_space_line_plans / ContractSpaceLinePlanPickerModal, DRAFT-time ekvivalent). */
  restrictToLineIds?: Set<string>;
  /** Strop/potrošnja po liniji TOČNO za prostor ovog radnog paketa (ne grana) — mora pratiti
   * backendov assertWithinSpacePlan. Bez ovoga nije moguće znati koliko se smije dodati. Potrošnja
   * već uključuje nespremljene promjene stavki ovog paketa. */
  spaceLinePlanUsageByLineId: Map<string, SpaceLinePlanUsage>;
  /** Odabrane stavke idu u nacrt stavki (NOVO) — spremaju se tek gumbom "Spremi stavke". */
  onAdd: (items: { line: ContractLine; plannedQty: number }[]) => void;
}

export const WorkPackageItemPickerModal = ({
  opened,
  onClose,
  contractId,
  existingContractLineIds,
  restrictToLineIds,
  spaceLinePlanUsageByLineId,
  onAdd
}: WorkPackageItemPickerModalProps) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [quantities, setQuantities] = useState<Map<string, number>>(new Map());

  // Potrošnja po prostoru se mijenja i iz drugih radnih paketa / sesija — pri svakom otvaranju
  // povući svježe stanje, da ponuđena količina odgovara onome što backend stvarno prihvaća.
  useEffect(() => {
    if (!opened || !contractId) return;
    invalidateContractSpaceUsage(queryClient, contractId);
    queryClient.invalidateQueries({ queryKey: getListContractLinesQueryKey(contractId) });
  }, [opened, contractId, queryClient]);

  const { data } = useListContractLines(contractId ?? '', {
    query: { enabled: opened && !!contractId }
  });
  const { data: unitsData } = useListUnits();
  const alreadyAdded = new Set(existingContractLineIds);
  const spaceUsage = (line: ContractLine): SpaceLinePlanUsage =>
    spaceLinePlanUsageByLineId.get(line.id ?? '') ?? { ceiling: 0, consumed: 0 };
  const remainingQty = (line: ContractLine) => {
    const usage = spaceUsage(line);
    return usage.ceiling - usage.consumed;
  };

  // Stavke koje su već potpuno raspoređene po CIJELOM ugovoru (u bilo kojem prostoru)
  // nema smisla nuditi ovdje — nema više slobodne količine iz razrade. Vrijedi za obje
  // vrste ugovora: razrada je referenca za planiranje (vidi WorkStructureService).
  const pickableLines: ContractLine[] = (data ?? []).filter(
    (line) =>
      !(line.id && alreadyAdded.has(line.id)) &&
      remainingQty(line) > 0 &&
      (!restrictToLineIds || (!!line.id && restrictToLineIds.has(line.id)))
  );
  const categories = Array.from(
    new Set(pickableLines.map((line) => line.category).filter(Boolean))
  ) as string[];

  const query = search.trim().toLowerCase();
  const lines: ContractLine[] = pickableLines.filter((line) => {
    if (categoryFilter && line.category !== categoryFilter) return false;
    if (!query) return true;
    return (
      line.itemCode?.toLowerCase().includes(query) ||
      line.description?.toLowerCase().includes(query)
    );
  });

  const selectableIds = lines.map((line) => line.id).filter((id): id is string => !!id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id));
  const maxQtyById = new Map(lines.map((line) => [line.id, remainingQty(line)]));

  // Odabrana stavka kreće od količine 0 (isto kao ponuda/ugovor/aktivnost) — korisnik sam upisuje.
  const toggle = (id?: string) => {
    if (!id) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setQuantities((prev) => {
      if (prev.has(id)) return prev;
      const next = new Map(prev);
      next.set(id, 0);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
    setQuantities((prev) => {
      const next = new Map(prev);
      if (!allSelected) {
        selectableIds.forEach((id) => {
          if (!next.has(id)) next.set(id, 0);
        });
      }
      return next;
    });
  };

  const setQuantity = (id: string, value: number) => {
    setQuantities((prev) => {
      const next = new Map(prev);
      next.set(id, value);
      return next;
    });
  };

  const reset = () => {
    setSearch('');
    setCategoryFilter(null);
    setSelected(new Set());
    setQuantities(new Map());
  };

  // Sigurnosna kočnica uz max/clamp na samom polju — ako je unesena količina
  // (iz bilo kojeg razloga, npr. dok Mantine NumberInput još drži sirovi
  // unos prije blur-a) veća od ugovorene, gumb za dodavanje se onemogući.
  // Odabrana stavka koja je nakon osvježavanja potpuno iskorištena nestane s popisa — ne šalje se.
  const pickableIds = new Set(pickableLines.map((line) => line.id));
  const selectedPickable = Array.from(selected).filter((id) => pickableIds.has(id));

  // 0 je samo početna vrijednost — svaka odabrana stavka mora dobiti količinu ≥ 1 prije dodavanja.
  const hasMissingQuantity = selectedPickable.some((id) => (quantities.get(id) ?? 0) < 1);

  const hasQuantityOverContracted = selectedPickable.some((id) => {
    const max = maxQtyById.get(id);
    return max != null && (quantities.get(id) ?? 0) > max;
  });

  const handleAdd = () => {
    const lineById = new Map(pickableLines.map((line) => [line.id, line]));
    onAdd(
      selectedPickable.flatMap((id) => {
        const line = lineById.get(id);
        return line ? [{ line, plannedQty: quantities.get(id) ?? 0 }] : [];
      })
    );
    reset();
    onClose();
  };

  return (
    <FormModal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      size={860}
      title={
        <Text fz={20} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('workPackages.picker.title')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        {pickableLines.length === 0 ? (
          <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
            {!restrictToLineIds
              ? t('workPackages.picker.emptyState')
              : restrictToLineIds.size === 0
                ? t('workPackages.picker.emptyStateRestricted')
                : t('workPackages.picker.emptyStateExhausted')}
          </Text>
        ) : (
          <>
            <Group gap={12} wrap="wrap">
              <div style={{ flex: 1, minWidth: 220 }}>
                <TextField
                  hideLabel
                  placeholder={t('workPackages.picker.searchPlaceholder')}
                  value={search}
                  onChange={(event) => setSearch(event.currentTarget.value)}
                />
              </div>
              <div style={{ width: 200 }}>
                <SelectField
                  hideLabel
                  placeholder={t('workPackages.picker.allCategories')}
                  clearable
                  data={[
                    { value: '', label: t('workPackages.picker.allCategories') },
                    ...categories.map((c) => ({ value: c, label: c }))
                  ]}
                  value={categoryFilter ?? ''}
                  onChange={(value) => setCategoryFilter(value || null)}
                />
              </div>
            </Group>

            <Stack gap={0} style={{ maxHeight: 360, overflowY: 'auto' }}>
              <Group
                gap={8}
                wrap="nowrap"
                py={4}
                style={{ borderBottom: '1px solid var(--color-border-soft)' }}
              >
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  disabled={selectableIds.length === 0}
                  onChange={toggleAll}
                  aria-label={t('workPackages.picker.selectAll')}
                />
                <Text fz={11} fw={600} w={90} style={{ color: 'var(--color-text-muted)' }}>
                  {t('workPackages.picker.colCode')}
                </Text>
                <Text fz={11} fw={600} style={{ flex: 1, color: 'var(--color-text-muted)' }}>
                  {t('workPackages.picker.colName')}
                </Text>
                <Text fz={11} fw={600} w={70} style={{ color: 'var(--color-text-muted)' }}>
                  {t('workPackages.picker.colUnit')}
                </Text>
                <Text
                  fz={11}
                  fw={600}
                  w={100}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {t('workPackages.picker.colContracted')}
                </Text>
                <Text
                  fz={11}
                  fw={600}
                  w={85}
                  ta="right"
                  style={{ color: 'var(--color-text-muted)' }}
                >
                  {t('workPackages.picker.colQty')}
                </Text>
              </Group>

              {lines.length === 0 && (
                <Text fz={13} py={24} ta="center" style={{ color: 'var(--color-text-muted)' }}>
                  {t('workPackages.picker.emptyState')}
                </Text>
              )}

              {lines.map((line, index) => {
                const isSelected = !!line.id && selected.has(line.id);
                const isOverLimit =
                  isSelected && (quantities.get(line.id ?? '') ?? 0) > remainingQty(line);
                return (
                  <Group
                    key={line.id}
                    gap={8}
                    wrap="nowrap"
                    py={10}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggle(line.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') toggle(line.id);
                    }}
                    style={{
                      cursor: 'pointer',
                      borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggle(line.id)}
                      onClick={(event) => event.stopPropagation()}
                    />
                    <Text fz={13} fw={600} w={90} style={{ color: 'var(--color-text-primary)' }}>
                      {line.itemCode}
                    </Text>
                    <Text
                      fz={13}
                      style={{ flex: 1, color: 'var(--color-text-secondary)' }}
                      truncate
                    >
                      {line.description}
                    </Text>
                    <Text fz={13} w={70} style={{ color: 'var(--color-text-muted)' }}>
                      {findUnitLabel(unitsData, line.unitId, i18n.language)}
                    </Text>
                    <Text
                      fz={13}
                      w={100}
                      ta="right"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {spaceUsage(line).consumed +
                        (isSelected ? (quantities.get(line.id ?? '') ?? 0) : 0)}
                      {' / '}
                      {spaceUsage(line).ceiling}
                    </Text>
                    <Box w={85} onClick={(event) => event.stopPropagation()}>
                      <NumberInput
                        size="xs"
                        hideControls
                        min={0}
                        step={1}
                        error={isOverLimit}
                        disabled={!isSelected}
                        value={line.id ? (quantities.get(line.id) ?? 0) : 0}
                        onChange={(value) => {
                          if (!line.id) return;
                          setQuantity(line.id, Math.max(0, Math.trunc(Number(value)) || 0));
                        }}
                      />
                    </Box>
                  </Group>
                );
              })}
            </Stack>

            <Text fz={12.5} style={{ color: 'var(--color-text-secondary)' }}>
              {t('itemsDraft.pickerNote')}
            </Text>
          </>
        )}

        <ModalFooter>
          {pickableLines.length === 0 ? (
            <Button
              onClick={() => {
                reset();
                onClose();
              }}
            >
              {t('common.ok')}
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  reset();
                  onClose();
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                disabled={
                  selectedPickable.length === 0 || hasMissingQuantity || hasQuantityOverContracted
                }
                onClick={handleAdd}
              >
                {t('workPackages.picker.addSelected', { count: selectedPickable.length })}
              </Button>
            </>
          )}
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
